import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EducationModulesManager = ({ modules }) => {
  const [selectedModule, setSelectedModule] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'sales', label: 'Sales' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'operations', label: 'Operations' }
  ];

  const getCompletionColor = (rate) => {
    if (rate >= 80) return 'text-success';
    if (rate >= 50) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2 border border-border">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1 md:mb-2">
            Financial Education Modules
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Content management with completion tracking
          </p>
        </div>
        <Button variant="default" iconName="Plus" iconPosition="left">
          Create New Module
        </Button>
      </div>
      <div className="space-y-3 md:space-y-4">
        {modules?.map((module) => (
          <div 
            key={module.id}
            className="bg-muted rounded-lg p-4 md:p-5 hover:shadow-elevation-2 transition-smooth"
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-start gap-3 mb-2 md:mb-3">
                  <div className={`p-2 rounded-lg ${module.status === 'active' ? 'bg-success/10' : 'bg-muted-foreground/10'}`}>
                    <Icon 
                      name={module.icon} 
                      size={20} 
                      className={module.status === 'active' ? 'text-success' : 'text-muted-foreground'}
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                      {module.title}
                    </h4>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {module.description}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-3 md:mt-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Enrolled</p>
                    <p className="text-sm md:text-base font-semibold text-foreground">{module.enrolled}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Completed</p>
                    <p className="text-sm md:text-base font-semibold text-foreground">{module.completed}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Completion Rate</p>
                    <p className={`text-sm md:text-base font-semibold ${getCompletionColor(module.completionRate)}`}>
                      {module.completionRate}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Avg. Duration</p>
                    <p className="text-sm md:text-base font-semibold text-foreground">{module.duration}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-row lg:flex-col gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  iconName="Users"
                  onClick={() => {
                    setSelectedModule(module);
                    setShowAssignModal(true);
                  }}
                  fullWidth
                >
                  Assign
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  iconName="BarChart3"
                  fullWidth
                >
                  Analytics
                </Button>
              </div>
            </div>

            <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-border">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Progress</span>
                <span>{module.completionRate}%</span>
              </div>
              <div className="w-full h-2 bg-background rounded-full mt-2 overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full transition-smooth"
                  style={{ width: `${module.completionRate}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {showAssignModal && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-background/80">
          <div className="bg-card rounded-xl shadow-elevation-5 p-6 md:p-8 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">
                Assign Module
              </h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-1 rounded-lg hover:bg-muted transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Module Name"
                value={selectedModule?.title}
                disabled
              />
              <Select
                label="Assign to Department"
                options={departmentOptions}
                placeholder="Select department"
              />
              <Input
                label="Due Date"
                type="date"
              />
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAssignModal(false)}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button 
                  variant="default"
                  onClick={() => {
                    setShowAssignModal(false);
                    console.log('Module assigned');
                  }}
                  fullWidth
                >
                  Assign Module
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationModulesManager;